import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import {
  Box,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { SearchSection } from "../components/SearchSection";
import type {
  DataGridRow,
  StoredPicturesDownloadCondition,
  SearchCondition,
} from "../types";
import { RightsSectionOtsukuFeedGrid } from "../components/RightsSectionOtsukuFeedGrid";
import { getRightsSectionOtsukuListAPI } from "../api";
import { useRightsSectionOtsukuReasonOptions } from "../hooks/useRightsSectionOtsukuReasonOptions";
import { UserConfigGetAPI } from "@/features/accountSettings/api/UserConfigAPI";
import { useApiClient } from "@/hooks/useApiClient";
import { CustomModal } from "@/components/Modal";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { GetPrefecturesAPI } from "@/api";
import { downloadStoredPicturesAPI } from "@/api/downloadStoredPicturesAPI";
import { type IPrefecture } from "@/types/prefectures";
import { type GridSelectionModel } from "@mui/x-data-grid-pro";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import { toast } from "react-toastify";
import { LakersBackendApiRequestTimeoutError } from "@/utils/errors";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { formatNumberWithComma } from "@/utils/formatNumber";

const MAX_DOWNLOAD_COUNT = 100;

const RightsSectionOtsuku: React.FC = () => {
  const { apiClient } = useApiClient();
  const navigate = useNavigate();
  const isReadOnly = useReadOnlyUser();

  // ユーザコンフィグを取得するフック
  const userConfig = UserConfigGetAPI();

  // 結果表示部分に表示する行のState
  const [rows, setRows] = useState<DataGridRow[]>([]);

  // ローディングの状態を管理するState
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // ダウンロード内容のState
  const defaultDownloadCondition: StoredPicturesDownloadCondition = {
    billableData: [],
    noBillableData: [],
  };
  const [downloadCondition, setDownloadCondition] =
    useState<StoredPicturesDownloadCondition>(defaultDownloadCondition);

  // ダウンロード確認モーダル表示のState
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // 都道府県リストのState
  const [prefectures, setPrefectures] = useState<IPrefecture[]>([]);

  // 登記原因プルダウンの選択肢
  const { reasonOptions } = useRightsSectionOtsukuReasonOptions();

  // 選択された行のIDを管理するState
  const [selectedRowIds, setSelectedRowIds] = useState<GridSelectionModel>([]);

  // 検索項目のState
  const [searchCondition, setSearchCondition] = useState<SearchCondition>();

  // 一括ダウンロード時にリユース優先でするか、取得済優先でダウンロードするかのラジオを表示するかのState
  const [showReusePriorityDownloadOption, setShowReusePriorityDownloadOption] =
    useState<boolean>(false);

  // リユース優先で一括ダウンロードするかどうかのState
  const [isReusePriorityDownload, setIsReusePriorityDownload] =
    useState<boolean>(true);

  useEffect(() => {
    (async () => {
      // 都道府県リストをAPIから取得する。
      const prefectures = await GetPrefecturesAPI(apiClient);
      setPrefectures(prefectures);
    })();
  }, []);

  const handleSearch = (searchCondition: SearchCondition): void => {
    (async () => {
      setIsLoading(true);
      try {
        const rows = await getRightsSectionOtsukuListAPI(
          apiClient,
          searchCondition
        );
        setSearchCondition(searchCondition);
        setRows(rows);
      } catch (error) {
        if (error instanceof LakersBackendApiRequestTimeoutError) {
          toast.info(error.message);
          return;
        }

        toast.error(
          "検索に失敗しました。しばらく経ってから再度お試しください。"
        );
      } finally {
        setIsLoading(false);
      }
    })();
  };

  /**
   * ダウンロード条件を計算する共通関数
   */
  const calculateDownloadCondition = (
    isReusePriority: boolean
  ): {
    newDownloadCondition: StoredPicturesDownloadCondition;
    shouldShowOption: boolean;
  } => {
    const newDownloadCondition: StoredPicturesDownloadCondition = {
      billableData: [],
      noBillableData: [],
    };
    let shouldShowOption = false;

    selectedRowIds.forEach((value) => {
      const row = rows.find((rowElement) => rowElement.id === value);
      if (row != null) {
        // receivedDateがnullの行はダウンロード対象外
        if (row.receivedDate == null) return;

        // リユース優先ダウンロードオプションを表示するかどうかを判定
        if (
          row.lastUserAcquisitionDate &&
          row.lastUserAcquisitionDate < row.receivedDate
        ) {
          shouldShowOption = true;
        }

        if (isReusePriority) {
          if (
            row.lastUserAcquisitionDate === null ||
            row.lastUserAcquisitionDate < row.receivedDate
          ) {
            newDownloadCondition.billableData.push({
              id: row.id,
              price: row.chargeAmount,
              receivedId: row.receivedId,
            });
          } else {
            newDownloadCondition.noBillableData.push({
              id: row.id,
            });
          }
        } else {
          if (row.lastUserAcquisitionDate === null) {
            newDownloadCondition.billableData.push({
              id: row.id,
              price: row.chargeAmount,
              receivedId: row.receivedId,
            });
          } else {
            newDownloadCondition.noBillableData.push({
              id: row.id,
            });
          }
        }
      }
    });

    return { newDownloadCondition, shouldShowOption };
  };

  /**
   * ダウンロードボタンを押したときにIDと金額を計算する関数
   */
  const handleModalOpen = (): void => {
    setIsReusePriorityDownload(true);

    const { newDownloadCondition, shouldShowOption } =
      calculateDownloadCondition(true);

    setDownloadCondition(newDownloadCondition);
    setShowReusePriorityDownloadOption(shouldShowOption);
    setIsOpen(true);
  };

  /**
   * 課金対象データの金額を合算する。
   */
  const sumChargeAmount = (): string => {
    const price = downloadCondition.billableData.reduce((a, x) => {
      return a + x.price;
    }, 0);
    return `${price.toLocaleString()}円`;
  };

  return (
    <PagePaper>
      {/* ページタイトル */}
      <Box sx={{ display: "flex", mb: 3 }}>
        <FindInPageIcon sx={{ mr: 1 }} fontSize="large" />
        <PageTitle>権利部（乙区）情報から検索する</PageTitle>
      </Box>

      {/* 検索部分 */}
      <SectionPaper>
        <SearchSection
          handleSearch={handleSearch}
          prefectures={prefectures}
          reasonOptions={reasonOptions}
          isLoading={isLoading}
        />
      </SectionPaper>

      {/* 余白 */}
      <Box sx={{ mt: 3 }} />

      {/* ダウンロードボタン */}
      <Box
        sx={{
          my: 3,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <ReadOnlyTooltip isReadOnly={isReadOnly}>
          <Button
            label="一括ダウンロード"
            disabled={
              isReadOnly ||
              selectedRowIds.length === 0 ||
              selectedRowIds.length > MAX_DOWNLOAD_COUNT
            }
            onClick={handleModalOpen}
            variant={ButtonVariantOption.Outlined}
          />
        </ReadOnlyTooltip>
      </Box>

      {/* 最大ダウンロード件数文言表示 */}
      <Box
        sx={{
          my: 1,
          display: "flex",
          justifyContent: "flex-start",
        }}
      >
        <Typography variant="body1">
          一括ダウンロードの上限は{formatNumberWithComma(MAX_DOWNLOAD_COUNT)}
          件です。
        </Typography>
      </Box>

      {/* 結果表示部分 */}
      <SectionPaper>
        <RightsSectionOtsukuFeedGrid
          rows={rows}
          setIsOpen={setIsOpen}
          setDownloadCondition={setDownloadCondition}
          apiClient={apiClient}
          setSelectedRowIds={setSelectedRowIds}
          navigate={navigate}
          loading={isLoading}
          isReadOnly={isReadOnly}
        />
      </SectionPaper>

      {/* 課金確認モーダル */}
      <CustomModal
        isOpen={isOpen}
        handleClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setIsOpen(false);
        }}
        ariaLabelledby="download-modal"
        ariaDescribedby="download-modal-description"
      >
        <PageTitle>課金確認</PageTitle>
        <Box>
          {showReusePriorityDownloadOption && (
            <FormControl sx={{ my: 1 }}>
              <RadioGroup
                row
                value={isReusePriorityDownload ? "reuse" : "used"}
                onChange={(e) => {
                  const newIsReusePriority = e.target.value === "reuse";
                  setIsReusePriorityDownload(newIsReusePriority);
                  const { newDownloadCondition } =
                    calculateDownloadCondition(newIsReusePriority);
                  setDownloadCondition(newDownloadCondition);
                }}
              >
                <FormControlLabel
                  value="reuse"
                  control={<Radio />}
                  label="リユース登記優先で一括ダウンロードする"
                />
                <FormControlLabel
                  value="used"
                  control={<Radio />}
                  label="取得済登記優先で一括ダウンロードする"
                />
              </RadioGroup>
            </FormControl>
          )}

          {userConfig === undefined ? null : userConfig.isPriceHidden ? (
            <>
              <Typography>課金が発生します。</Typography>
              <Typography>よろしいですか？</Typography>
            </>
          ) : (
            <>
              <Typography>{sumChargeAmount()}課金されます。</Typography>
              <Typography>よろしいですか？</Typography>

              <Box
                sx={{
                  my: 3,
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead sx={{ bgcolor: "whitesmoke" }}>
                      <TableRow>
                        <TableCell>名前</TableCell>
                        <TableCell>件数</TableCell>
                        <TableCell>金額</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>課金対象</TableCell>
                        <TableCell>
                          {formatNumberWithComma(
                            downloadCondition.billableData.length
                          )}
                        </TableCell>
                        <TableCell>{sumChargeAmount()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>課金対象外</TableCell>
                        <TableCell>
                          {formatNumberWithComma(
                            downloadCondition.noBillableData.length
                          )}
                        </TableCell>
                        <TableCell>-</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}

          <Box
            sx={{
              mr: 2,
              my: 3,
              display: "flex",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              label="いいえ"
              onClick={() => {
                setDownloadCondition(defaultDownloadCondition);
                setIsOpen(false);
              }}
              variant={ButtonVariantOption.Outlined}
            />
            <SubmitButton
              label="はい"
              onClick={(_event, cancelLoading) => {
                void (async () => {
                  try {
                    await downloadStoredPicturesAPI(
                      apiClient,
                      downloadCondition,
                      navigate
                    );
                    if (searchCondition) {
                      const response = await getRightsSectionOtsukuListAPI(
                        apiClient,
                        searchCondition
                      );
                      setRows(response);
                    }
                  } finally {
                    setDownloadCondition(defaultDownloadCondition);
                    setIsOpen(false);
                    cancelLoading();
                  }
                })();
              }}
              timeout={30000}
              variant={ButtonVariantOption.Contained}
            />
          </Box>
        </Box>
      </CustomModal>
    </PagePaper>
  );
};

export const RightsSectionOtsukuRoutes: React.FC = () => (
  <Routes>
    <Route path="" element={<RightsSectionOtsuku />} />
  </Routes>
);
